import React from 'react';
import styled from 'styled-components';
import { Sidebar, SidebarSection, Text } from 'bench-internal-theme';
import { Balance, Connection } from '../types';
import { colors } from 'bench-style-constants';
import { formatDate } from 'date-functions';
import LedgerSelect from './LedgerSelect';
import UsageTypeSelect from './UsageTypeSelect';

export const SidebarSectionLayout = styled.div`
  display: grid;
  gap: 16px;
`;

export const FormGroup = styled.div`
  align-items: center;
  display: grid;
  gap: 4px;
  grid-template-columns: 130px 1fr;
  padding-bottom: 4px;
`;

type ConnectionLedgerMappingModalProps = {
  connection: Connection;
  closeSidebar: () => void;
};

const reThousandBoundry = /\B(?=(\d{3})+(?!\d))/g;

const LedgerMappingSidebar = ({ connection, closeSidebar }: ConnectionLedgerMappingModalProps) => {
  const getCurrencySymbol = (currencyCode: string) => {
    const currencySymbols = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      JPY: '¥',
      AUD: 'A$',
      CAD: 'C$',
      CHF: 'Fr',
      CNY: '¥',
      SEK: 'kr',
      NZD: 'NZ$',
      NOK: 'kr',
      KRW: '₩',
      INR: '₹',
      MXN: 'Mex$',
      BRL: 'R$',
      RUB: '₽',
      ZAR: 'R',
      SGD: 'S$',
      HKD: 'HK$',
    };
    return currencyCode in currencySymbols ? currencySymbols[currencyCode] : '$';
  };

  const formatCurrency = (amount: number, currencyCode: string): string => {
    const absAmount =
      getCurrencySymbol(currencyCode) + Math.abs(amount).toFixed(2).replace(reThousandBoundry, ',');
    if (amount < 0) {
      return `(${absAmount})`;
    }
    return absAmount;
  };

  const showLatestBalance = (balances: Balance[], currencyCode: string) => {
    if (balances.length !== 0) {
      return formatCurrency(balances[0].amount / 100, currencyCode);
    }
  };

  const showLatestBalanceDate = (balances: Balance[]) => {
    if (balances.length !== 0) {
      return formatDate(new Date(balances[0].sourceSyncTime));
    }
  };

  const formatAccountName = account => {
    if (account?.number) {
      return `${account.name} - ${account.number}`;
    }
    return account.name;
  };

  return (
    <Sidebar dismiss={closeSidebar} headerContent={`Edit ${connection.financialInstitutionName}`}>
      {connection.accounts?.length === 0 ? (
        <SidebarSection>
          <Text styleType="small" color={colors.cloud}>
            No accounts found for this connection.
          </Text>
        </SidebarSection>
      ) : (
        connection.accounts.map((account, index) => (
          <SidebarSection key={index} hasBorderBottom={index !== connection.accounts.length - 1}>
            <SidebarSectionLayout>
              <Text styleType="mediumBold">{formatAccountName(account)}</Text>
              <FormGroup>
                <Text color={colors.cloud} styleType="small" uppercase>
                  Status
                </Text>
                <Text styleType="small">{account.isActive ? 'Active' : 'Inactive'}</Text>
                <Text color={colors.cloud} styleType="small" uppercase>
                  Balance
                </Text>
                <Text styleType="small">
                  {account.isActive && !account.isPersonal
                    ? showLatestBalance(account.balances.data, account.defaultCurrency ?? 'USD')
                    : 'N/A'}
                </Text>
                <Text color={colors.cloud} styleType="small" uppercase>
                  Balance Date
                </Text>
                <Text styleType="small">
                  {account.isActive && !account.isPersonal
                    ? showLatestBalanceDate(account.balances.data)
                    : 'N/A'}
                </Text>
              </FormGroup>
              <FormGroup>
                <Text color={colors.cloud} styleType="small" uppercase>
                  Ledger
                </Text>
                <LedgerSelect account={account} connectionSource={connection.source} />
                <Text color={colors.cloud} styleType="small" uppercase>
                  Usage
                </Text>
                <UsageTypeSelect account={account} />
              </FormGroup>
            </SidebarSectionLayout>
          </SidebarSection>
        ))
      )}
    </Sidebar>
  );
};

export default LedgerMappingSidebar;
